import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { PersonPicker } from '../person-picker/PersonPicker';
import './filter.css';

export interface FilterField {
  key: string;
  label: string;
  /**
   * „people“ wählt mehrere Personen über ein Suchfeld mit Vorschlägen (für viele Mitarbeiter), „multi“ mehrere Einträge aus einer kurzen Liste mit Häkchen.
   * Beide haben als Wert die gewählten Einträge, durch Kommas getrennt.
   */
  type: 'select' | 'text' | 'date' | 'people' | 'multi';
  /** Für „select“, „multi“ und „people“. Bei „select“ steht der Eintrag mit dem Wert '' für „kein Filter“ (oder die Vorgabe der Liste). */
  options?: { value: string; label: string }[];
  placeholder?: string;
}

interface Props {
  search: { value: string; placeholder: string; onSubmit: (value: string) => void };
  fields: FilterField[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** Setzt alle Filter und die Suche zurück */
  onReset: () => void;
}

/**
 * Suche und Filter für Listen, nach dem Vorbild von Business Central: Neben der Suche steht die Schaltfläche „Filter“, die einen Bereich mit
 * Auswahl- und Eingabefeldern einblendet. Auf dem Handy erscheint er als Blatt von unten. Gesetzte Filter stehen als Schilder unter der Suche
 * und lassen sich einzeln entfernen. Ein Filter gilt als gesetzt, sobald sein Wert nicht leer ist. Der Baustein gehört keinem Modul, damit
 * alle Listen ihn nutzen.
 */
export function FilterBar({ search, fields, values, onChange, onReset }: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(search.value);
  const panelId = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => setText(search.value), [search.value]);

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };

  // Escape schließt, beim Öffnen springt der Fokus in den Bereich
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('select, input')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const labelOf = (f: FilterField, value: string) => (f.type === 'select' || f.type === 'multi' || f.type === 'people' ? (f.options?.find((o) => o.value === value)?.label ?? 'Nicht mehr verfügbar') : value);
  const active = fields.filter((f) => (values[f.key] ?? '') !== '');
  const searching = search.value !== '';
  const count = active.length;

  function submit(event: FormEvent) {
    event.preventDefault();
    search.onSubmit(text.trim());
  }

  return (
    <>
      <div className="listbar">
        <form className="listbar__search" role="search" onSubmit={submit}>
          <input className="input" type="search" placeholder={search.placeholder} value={text} onChange={(e) => setText(e.target.value)} aria-label="Suchen" />
          <button className="btn btn--secondary" type="submit">Suchen</button>
        </form>

        {fields.length > 0 && (
          <div className="listbar__filter">
            <button
              ref={button}
              type="button"
              className="btn btn--secondary filter-button"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setOpen((v) => !v)}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 4h18l-7 8.5V19l-4 2v-8.5z" />
              </svg>
              <span className="filter-button__label">Filter</span>
              {count > 0 && <span className="filter-count" aria-label={`${count} Filter gesetzt`}>{count}</span>}
            </button>

            {open && (
              <>
                <div className="filter-backdrop" onClick={close} />
                <div id={panelId} ref={panel} className="filter-panel" role="dialog" aria-label="Filter">
                  <div className="filter-panel__head">
                    <strong>Filter</strong>
                    <button type="button" className="filter-panel__close" onClick={close} aria-label="Filter schließen">×</button>
                  </div>
                  <div className="filter-panel__body">
                    {fields.map((f) => (
                      <FilterInput key={f.key} field={f} value={values[f.key] ?? ''} onChange={(v) => onChange(f.key, v)} />
                    ))}
                  </div>
                  <div className="filter-panel__foot">
                    <button type="button" className="btn btn--secondary" onClick={onReset} disabled={count === 0 && !searching}>Zurücksetzen</button>
                    <button type="button" className="btn btn--primary" onClick={close}>Fertig</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {(count > 0 || searching) && (
        <ul className="filter-chips" aria-label="Gesetzte Filter">
          {searching && (
            <li>
              <button type="button" className="filter-chip" onClick={() => search.onSubmit('')} aria-label={`Suche „${search.value}“ entfernen`}>
                Suche: {search.value} <span aria-hidden="true">×</span>
              </button>
            </li>
          )}
          {active.flatMap((f) => {
            // Bei Mehrfachauswahl steht jede gewählte Person oder Art als eigenes Schild, und ein Klick nimmt nur diese heraus
            if (f.type === 'people' || f.type === 'multi') {
              const chosen = values[f.key]!.split(',').filter(Boolean);
              return chosen.map((v) => (
                <li key={`${f.key}:${v}`}>
                  <button type="button" className="filter-chip" onClick={() => onChange(f.key, chosen.filter((x) => x !== v).join(','))} aria-label={`${labelOf(f, v)} aus ${f.label} entfernen`}>
                    {f.label}: {labelOf(f, v)} <span aria-hidden="true">×</span>
                  </button>
                </li>
              ));
            }
            return [
              <li key={f.key}>
                <button type="button" className="filter-chip" onClick={() => onChange(f.key, '')} aria-label={`Filter ${f.label} entfernen`}>
                  {f.label}: {labelOf(f, values[f.key]!)} <span aria-hidden="true">×</span>
                </button>
              </li>,
            ];
          })}
        </ul>
      )}
    </>
  );
}

/** Ein Feld im Filterbereich. Auswahl und Datum gelten sofort, ein Textfeld gilt mit Enter oder beim Verlassen des Feldes. */
function FilterInput({ field, value, onChange }: { field: FilterField; value: string; onChange: (value: string) => void }) {
  const id = useId();
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  if (field.type === 'people') {
    const chosen = value.split(',').filter(Boolean);
    return (
      <PersonPicker
        label={field.label}
        placeholder={field.placeholder ?? 'Name eingeben …'}
        options={(field.options ?? []).map((o) => ({ id: o.value, name: o.label }))}
        value={chosen}
        onChange={(ids) => onChange(ids.join(','))}
      />
    );
  }
  if (field.type === 'multi') {
    const chosen = value.split(',').filter(Boolean);
    return (
      <fieldset className="field filter-multi">
        <legend>{field.label}</legend>
        {(field.options ?? []).map((o) => (
          <label key={o.value} className="check">
            <input type="checkbox" checked={chosen.includes(o.value)} onChange={(e) => onChange((e.target.checked ? [...chosen, o.value] : chosen.filter((x) => x !== o.value)).join(','))} />
            <span>{o.label}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>{field.label}</label>
      {field.type === 'select' && (
        <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {field.type === 'date' && <input id={id} className="input" type="date" value={value} onChange={(e) => onChange(e.target.value)} />}
      {field.type === 'text' && (
        <input
          id={id}
          className="input"
          value={draft}
          placeholder={field.placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => draft.trim() !== value && onChange(draft.trim())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onChange(draft.trim());
          }}
        />
      )}
    </div>
  );
}
